import clsx from 'clsx'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import SocialInstagramIcon from '~/src/components/SocialInstagramIcon'
import SocialTikTokIcon from '~/src/components/SocialTikTokIcon'
import SocialXIcon from '~/src/components/SocialXIcon'
import useInIframe from '~/src/hooks/useInIframe'
import DupeLogo from '~/src/components/svg/DupeLogo'
import { cn } from '~/src/lib/utils'
import { useNavClickTracker } from '~/src/hooks/useNavClickTracker'

const DarkModeToggle = dynamic(
  () => import('~/src/components/DarkModeToggle'),
  { ssr: false }
)

const currentYear = new Date().getFullYear()

interface FooterProps extends React.HTMLProps<HTMLDivElement> {
  hideFooter?: boolean
  shadow?: boolean
}

const Footer: React.FC<FooterProps> = ({
  className,
  hideFooter,
  shadow,
  ...props
}) => {
  const { inIframe, handleOnClick } = useInIframe()
  const trackNavClick = useNavClickTracker('footer')

  if (hideFooter) return null

  return (
    <footer
      className={clsx(
        'relative max-h-[800px] overflow-hidden pb-[250px] pt-32 md:max-h-[1000px] md:px-16 md:pb-[600px] md:pt-64',
        className,
        {
          'shadow-[inset_0_1px_0_0_rgba(0,0,0,0.05),inset_0_25px_50px_-12px_rgba(0,0,0,0.05)]':
            shadow,
          'dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05),inset_0_25px_50px_-12px_rgba(0,0,0,0.05)]':
            shadow,
        }
      )}
      onClick={trackNavClick}
      {...props}
    >
      <div className='relative z-10 mx-auto max-w-screen-2xl space-y-8 md:space-y-16'>
        <div className='space-y-4 px-4'>
          <div className='grid grid-cols-2 flex-wrap justify-between gap-8 md:flex md:gap-16'>
            {/* Navigation Links */}
            <div className='flex flex-col'>
              <div className='mb-1 text-sm opacity-70'>Company</div>
              <Link
                href='/about'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                About
              </Link>
              <Link
                href='/blog'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Blog
              </Link>
              <Link
                href='/press'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Press
              </Link>
              <Link
                href='/faq'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                FAQ & Contact
              </Link>
            </div>

            {/* Partnerships */}
            <div className='flex flex-col'>
              <div className='mb-1 text-sm opacity-70'>Partnerships</div>
              <Link
                href='/partners'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                For brands
              </Link>
              <Link
                href='/creators'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                For creators
              </Link>
            </div>

            {/* Apps & Tools */}
            <div className='flex flex-col'>
              <div className='mb-1 text-sm opacity-70'>Apps & Tools</div>
              <Link
                href='/steals'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Steals
              </Link>
              <Link
                href='/app'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Dupe App
              </Link>
              <Link
                href='/app'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Get the Extension
              </Link>
              <Link
                href='/reports'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Dupe Reports
              </Link>
              <div className='flex py-2'>{!inIframe && <DarkModeToggle />}</div>
            </div>

            {/* $DUPE */}
            <div className='flex flex-col'>
              <div className='mb-1 text-sm opacity-70'>$DUPE</div>
              <Link
                href='/$dupe'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Dupe on Solana
              </Link>
              <Link
                href='/$dupe/rewards'
                onClick={handleOnClick}
                className='py-2 text-sm hover:underline'
              >
                Ways to Earn
              </Link>
            </div>

            {/* Social */}
            <div className='flex flex-col'>
              <div className='mb-1 text-sm opacity-70'>Follow</div>
              <div className='flex items-center space-x-2'>
                <Link
                  href='https://www.instagram.com/dupe_com'
                  onClick={handleOnClick}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='py-2 pr-4'
                >
                  <SocialInstagramIcon height={24} />
                </Link>
                <Link
                  href='https://x.com/dupedotcom'
                  onClick={handleOnClick}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='py-2 pr-4'
                >
                  <SocialXIcon height={24} />
                </Link>
                <Link
                  href='https://www.tiktok.com/@dupedotcom'
                  onClick={handleOnClick}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='py-2 pr-4'
                >
                  <SocialTikTokIcon height={24} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className='flex items-center justify-between px-4'>
          <div className='text-sm uppercase'>© {currentYear} Dupe</div>
          <div className='flex items-center space-x-10'>
            <Link
              href='/legal/privacy'
              onClick={handleOnClick}
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm hover:underline'
            >
              Privacy
            </Link>
            <Link
              href='/legal/terms'
              onClick={handleOnClick}
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm hover:underline'
            >
              Terms
            </Link>
          </div>
        </div>
      </div>

      <div className='pointer-events-none absolute -inset-x-2 -bottom-6 mx-auto flex h-[200px] max-w-screen-2xl items-center justify-center md:-inset-x-4 md:h-[570px]'>
        {/* Sized purely via CSS (w-full h-auto) — an explicit width='auto'
            attribute is invalid SVG and logs a console error. */}
        <DupeLogo className='relative z-10 h-auto w-full text-white dark:text-black' />
        <div
          className={cn(
            'blur-[50px] md:blur-[100px]',
            'absolute top-0 z-0 h-[200%] w-[150dvw] rounded-t-[9999px]'
          )}
          style={{
            opacity: 0.4,
            backgroundColor: 'rgba(138, 242, 255, 0.60)',
          }}
        />
      </div>
    </footer>
  )
}

export default Footer
